// Plan de controle backend : interface stable + selecteur mock/real via env.
// L'impl reelle (RealBackendClient) appelle api.rmf.tf via le proxy Vite en dev et directement
// en prod (CORS strict configure pour https://rmf.tf cote backend).

import { MockBackendClient } from './mock-backend-client';
import { RealBackendClient } from './real-backend-client';

/** Etat affiche par /welcome pendant le wake-up. La chaine de readiness suit les 4 phases
 *  documentees dans scripts/e2e-rmf-full.sh : (1) /start retourne le DNS, (2) /status passe a
 *  state=ready (control_plane uvicorn up), (3) /health/full passe a phase=fully_ready
 *  (rmf-web HTTP bound), (4) /api/fleets >= expectedFleets ET /api/building_map=200 (chaine
 *  RMF complete, dispatch jouable). On ne signale `ready` au consommateur qu'apres la phase
 *  4 pour eviter les 422 random sur dispatch precoce. */
export type BackendPhase =
  | 'idle' // pas encore demarre
  | 'starting' // requete /start envoyee, instance en train de se reveiller
  | 'waiting' // /status retourne booting, EC2 en cold start (~45 s)
  | 'rmf_initializing' // /status=ready mais /health/full pas encore fully_ready
  | 'rmf_loading' // /health/full=fully_ready mais fleets/building_map pas encore servis
  | 'ready' // toute la chaine RMF est dispatchable
  | 'error'; // echec, retry possible

/** Reponse 200 ou 202 du POST /start (champs `dns` et `instance_id` du JSON Lambda). */
export type SessionInfo = {
  dns: string;
  instanceId: string;
  /**
   * True si /start a retourne 200 (entree DDB deja connue pour ce sub, idempotence ADR 020),
   * false si 202 (creation neuve). Attention : un 200 ne garantit PAS que l'EC2 est reellement
   * up (cas typique : F5 sur /welcome pendant le boot -> 200 alors que l'instance demarre
   * encore). Le passage en phase=ready se base toujours sur /status (sonde HTTPS publique
   * https://<dns>/health depuis AWS, avec validation du cert). Garde ce champ pour
   * l'observabilite et un eventuel UX "session resumee" futur.
   */
  wasExisting: boolean;
};

/** Reponse 200 du POST /stop. */
export type StopResult = {
  durationSeconds: number;
};

/**
 * Etat de la session vue par la Lambda /status. Cf brief backend v0.1.22 :
 * - no_session : aucune session active pour ce sub (rare apres /start, possible si sweep
 *   a coupe entre /start et le 1er /status)
 * - booting : EC2 en train de boot, control_plane uvicorn pas encore up, ou tout echec de la
 *   sonde HTTPS /health, dont un cert TLS de session expire ou invalide (indistinguable ici)
 * - ready : la session est prete, le frontend peut transitionner en phase 'ready'
 * - failed : la session a echoue (EC2 terminated unexpectedly, control_plane crash...).
 *   `reason` contient l'explication.
 */
export type SessionState = 'no_session' | 'booting' | 'ready' | 'failed';

/** Reponse 200 du GET /status. La Lambda sonde elle-meme https://<dns>/health en HTTPS
 *  public depuis AWS, avec validation du cert, donc le state reflete la realite reelle (pas
 *  une heuristique cote browser). Limite : 'ready' ne prouve pas que le navigateur reconnait
 *  la chaine du cert (magasin de confiance different, cf ADR 035). */
export type SessionStatus = {
  state: SessionState;
  instanceId: string | null;
  dns: string | null;
  elapsedSeconds: number | null;
  reason?: string;
};

/** Phase rapportee par GET https://<dns>/health/full (no auth). Indique l'avancement du boot
 *  RMF cote control_plane : `starting` = uvicorn pas encore ready, `ready` = control_plane up
 *  mais rmf-web pas encore HTTP bound, `fully_ready` = rmf-web a fini son lifespan FastAPI et
 *  ecoute sur le port 8000, `failed` = un sous-process critique a crashe. Source de verite
 *  pour la phase 3 du polling /welcome (cf scripts/e2e-rmf-full.sh T2). */
export type HealthPhase = 'starting' | 'ready' | 'fully_ready' | 'failed';

/** Reponse du GET /health/full. `phaseHistory` (snake_case backend `phase_history`) expose la
 *  duree de chaque phase pour observabilite : non utilise par la logique front, mais propage
 *  pour un eventuel panel debug. Snake_case backend mappe en camelCase ici. */
export type HealthFull = {
  phase: HealthPhase;
  phaseHistory?: Record<string, number>;
};

/**
 * Reponse 200 du GET /quota. Quota cumule sur 24h glissantes (pas sur le calendar day).
 * Le backend fait : used = sum(usage_history sur 24h) + duration_session_courante_si_active.
 * Cf ADR 020 + brief MVP-1.D.
 */
export type QuotaInfo = {
  usedSeconds: number;
  limitSeconds: number;
  remainingSeconds: number;
  hasActiveSession: boolean;
  /** ISO 8601 UTC, null si pas de session active. */
  activeSessionStartedAt: string | null;
  /** Duree en secondes de la session courante au moment de la lecture, null si inactive. */
  activeSessionDurationSeconds: number | null;
};

/**
 * Reponse 200 du GET https://<session-dns>/api/auth/rmf-web-token. JWT HS256 trusted emis par
 * le reverse-proxy control_plane (cf ADR 032). Sert a authentifier Socket.IO (qui ne peut pas
 * se faire swap son token par le proxy car le packet CONNECT est dans Engine.IO non-decode) et
 * les REST `/api/*` upstream rmf-web. TTL court (~15 min) ; re-fetchable autant que necessaire
 * tant que le JWT Cognito est valide. Snake_case backend (`expires_in`) mappe vers camelCase ici.
 */
export type RmfWebToken = {
  token: string;
  expiresIn: number;
};

/**
 * Snapshot du WorldSwitcher control_plane (cf ADR 030). Retourne par GET /world-status (no auth).
 * Snake_case backend mappe vers camelCase ici.
 */
export type WorldStatus = {
  /** World actuellement actif (source of truth pour le rendu 3D). */
  currentWorld: string;
  /** True pendant la transition entre POST /switch-world et fin du restart systemd (~15-25s). */
  switching: boolean;
  /** World cible si transition en cours, null sinon. */
  targetWorld: string | null;
  /** ISO 8601 UTC. ETA fin du switch en cours, ou timestamp du dernier switch termine. */
  expectedReadyAt: string | null;
  /** Message d'erreur du dernier switch rate (DBus timeout typique). null si tout OK. */
  lastError: string | null;
  /** Allowlist hardcodee cote control_plane. Sert au front pour generer le dropdown. */
  allowedWorlds: readonly string[];
};

/** Reponse de POST /switch-world. `noop: true` quand target == current_world. */
export type SwitchWorldResult = {
  targetWorld: string;
  noop: boolean;
  estimatedDurationS: number;
  expectedReadyAt: string;
};

/** Categories de task supportees (rmf_demos V2). Cf rmf-task-dispatch.md. */
export type TaskCategory = 'patrol' | 'delivery' | 'clean';

/** Etats d'une task selon rmf_api.TaskState upstream. Mappes en fr cote UI. */
export type TaskStatus =
  | 'queued'
  | 'standby'
  | 'underway'
  | 'delayed'
  | 'completed'
  | 'canceled'
  | 'killed'
  | 'failed';

/**
 * Vue minimale d'une TaskState upstream. rmf_api expose bien plus de champs (events,
 * estimations, phases) — on garde seulement ce qui nourrit la UI. Champs typed `unknown`
 * pour ce qu'on n'utilise pas mais qu'on peut afficher en raw au debug.
 */
export type RmfTask = {
  /** ID unique alloue par RMF Dispatcher (court, ex `delivery.dispatch-abc123`). */
  bookingId: string;
  category: string;
  status: TaskStatus;
  /** Robot assigne, format `{ group: fleet_name, name: robot_name }`. null si pas encore assigne. */
  assignedTo: { group: string; name: string } | null;
  /** Unix millis du moment ou le dispatch_task_request a ete recu cote backend (= timestamp
   *  du clic "Dispatcher" cote front). Sert au tri "plus recent d'abord" dans la UI panel.
   *  Source : `booking.unix_millis_request_time`. null si absent (cas pathologique upstream). */
  requestedAtMillis: number | null;
  /** Raison de l'echec si la task a fini failed/canceled/killed. Extrait du premier element de
   *  `state.dispatch.errors[]` upstream rmf-web. null pour les tasks en cours ou completed.
   *  Sert a expliquer a l'user pourquoi sa task a echoue (ex "no fleet capable of clean zone
   *  X"). Cf rmf-task-dispatch.md backend pour les categories d'erreurs upstream. */
  failureReason: string | null;
  /** Unix millis de debut effectif (null si queued). */
  startedAtMillis: number | null;
  /** Unix millis de fin (null si pas encore terminee). */
  finishedAtMillis: number | null;
  /** Estimation totale en ms (set par RMF Dispatcher). null si non disponible. */
  estimateMillis: number | null;
  /** Detail brut (category-specific) pour debug / display avance. */
  detail?: unknown;
};

/**
 * Detail enrichi d'une task (GET /api/tasks/<id>/state). Etend RmfTask avec l'activite
 * concrete en cours d'execution (inferred depuis phases[active].events) pour piloter les
 * effets visuels frontend par robot.
 *
 * /!\ La structure backend (confirmee empiriquement 2026-05-28) :
 *   phases["1"].events est un dict {id -> Event}, avec :
 *     - event 0 : wrapper Sequence (toujours underway tant que la task tourne)
 *     - event 1 : "Go to [place:...]" (transit) -- completed quand le robot est arrive
 *     - event 2 : "Perform action" (cleaning effective) -- underway pendant le clean
 *     - events 3+ : sous-events du go_to (door open/close, waypoints intermediaires)
 *
 * On infere l'activite courante en cherchant un event `status: "underway"` dont le `name`
 * matche "Perform action" ou "Go to". Le wrapper Sequence est explicitement filtre.
 */
export type RmfTaskActivity = 'go_to_place' | 'perform_action' | null;
export type RmfTaskDetail = RmfTask & {
  /** Activite courante du robot, inferred depuis phases[active].events.
   *  - `'go_to_place'` : robot en transit vers le waypoint cible.
   *  - `'perform_action'` : robot stationne en train d'executer l'action metier (clean, etc.).
   *  - `null` : task pas encore demarree, terminee, ou shape inconnu (defense). */
  currentActivity: RmfTaskActivity;
  /** Noms des waypoints a venir (events `Go to [place:NAME]` non-completed, par ordre d'id
   *  croissant = ordre d'execution). Sert a reconstruire la polyline predictive cote front
   *  via lookup dans le building.levels[].waypoints (cf scene-robots.ts).
   *
   *  Solution C confirmee par backend 2026-05-28 : `path` n'est pas pousse par rmf-web V2.x
   *  (filtre Pydantic), mais les events du TaskState contiennent les waypoints semantiques
   *  prevus. Ligne droite entre waypoints, pas la trajectoire physique exacte — suffisant
   *  visuellement pour "ou va le robot". */
  upcomingWaypoints: readonly string[];
};

/** Payload POST /api/tasks/dispatch_task (cf rmf-task-dispatch.md). category-dependent description. */
export type DispatchTaskRequest =
  | {
      category: 'patrol';
      /** Au moins 2 waypoints nommes. Le robot fait des allers-retours entre eux N fois. */
      places: readonly string[];
      rounds: number;
    }
  | {
      category: 'delivery';
      pickup: { place: string; handler: string; payload: { sku: string; quantity: number } };
      dropoff: { place: string; handler: string; payload: { sku: string; quantity: number } };
    }
  | {
      category: 'clean';
      zone: string;
    };

/**
 * Vertex named (waypoint dispatchable) d'un level. Extrait du building_map upstream.
 *
 * Phase 8.10 : ce type n'est plus utilise par l'UI dispatch (la source de verite est devenue
 * worlds-metadata.json, cf doc backend Finding 1). On le garde pour la compat du retour
 * `getBuildingMap()` (sert au diagnostic readiness) mais les flags isCharger/pickupDispenser/
 * etc. ne sont PAS fiables cote upstream rmf-web (stripped a la serialisation).
 */
export type BuildingWaypoint = {
  name: string;
  level: string;
  x: number;
  y: number;
  isCharger: boolean;
  isHoldingPoint: boolean;
  isParkingSpot: boolean;
  pickupDispenser: string | null;
  dropoffIngestor: string | null;
};

/** Building map upstream rmf-web (GET /api/building_map). Vue minimaliste : on extrait les
 *  waypoints exploitables pour le dispatch. Les walls/doors/lifts viennent du building.yaml local. */
export type RmfBuildingMap = {
  name: string;
  waypoints: readonly BuildingWaypoint[];
};

export interface BackendClient {
  /**
   * Demarre une session backend. Idempotent cote backend (ADR 020) : un re-call retourne le
   * meme DNS si une session est deja active pour ce sub. Status HTTP 200 = idempotence,
   * 202 = creation neuve (cf SessionInfo.wasExisting).
   */
  start(): Promise<SessionInfo>;
  /**
   * Recupere le state actuel de la session via la Lambda /status (depuis v0.1.22). Le pattern
   * "control plane visible" : la Lambda sonde elle-meme https://<dns>/health en HTTPS public
   * depuis AWS, avec validation du cert, donc pas de souci cote browser (cache DNS local
   * NXDOMAIN, CORS preflight, TLS reset cold start).
   * Remplace l'ancien polling direct GET https://session-xxx.rmf.tf/health.
   */
  getStatus(): Promise<SessionStatus>;
  /**
   * Recupere l'etat detaille de la stack RMF cote control_plane (no auth). Sert au polling
   * de la phase 3 du readiness : apres /status=ready (control_plane up), on attend ici
   * phase=fully_ready qui indique que rmf-web a fini son lifespan FastAPI et bind le port
   * HTTP 8000. Sans ce wait, les REST /api/* et le dispatch repondent 401/refused en cascade.
   * Cf scripts/e2e-rmf-full.sh T2.
   */
  getHealthFull(): Promise<HealthFull>;
  /**
   * Recupere le nombre de fleets enregistrees aupres de rmf-web (GET /api/fleets, Bearer
   * Cognito). Sert au polling de la phase 4 du readiness : meme apres phase=fully_ready, les
   * fleet_adapter publient leur etat sur DDS avec ~30-60s de delai supplementaire avant que
   * rmf-web les expose via REST. Sans ce wait, dispatch_task retourne 422 status=failed
   * instantane. Cf scripts/e2e-rmf-full.sh T2.5 + bug task #36 upstream Open-RMF.
   */
  getFleetsCount(): Promise<number>;
  /** Termine la session courante (sub extrait du JWT cote backend). 404 -> SessionNotFoundError. */
  stop(): Promise<StopResult>;
  /**
   * Envoie un heartbeat au control_plane FastAPI pour maintenir la session vivante. Reset le
   * timer d'inactivite cote serveur (16 min avant shutdown auto, cf ADR 018). A appeler toutes
   * les 15 min cote front. 404 -> SessionNotFoundError (session deja terminee par sweep).
   */
  heartbeat(): Promise<void>;
  /**
   * Recupere le quota cumule de l'utilisateur sur les 24 dernieres heures glissantes
   * (cf ADR 020). Inclut la duree de la session courante si active. Lecture DDB cote backend,
   * latence ~50-100 ms.
   */
  getQuota(): Promise<QuotaInfo>;
  /**
   * Recupere un JWT trusted HS256 emis par le reverse-proxy control_plane (cf ADR 032). Sert
   * a authentifier la connexion Socket.IO via `io({ auth: { token } })` ET les REST `/api/*`
   * upstream rmf-web. TTL ~15 min cote backend ; le caller (sessionStore.getValidTrustedToken)
   * gere le cache + refresh transparent en single-flight, comme pour le JWT Cognito.
   * Necessite phase=ready cote front (le DNS de la session doit etre connu).
   */
  getRmfWebToken(): Promise<RmfWebToken>;
  /**
   * Recupere le snapshot du WorldSwitcher control_plane (no auth). Polled a 1Hz pendant un
   * switch en cours. Cf ADR 030 + control-plane-api.md § /world-status.
   */
  getWorldStatus(): Promise<WorldStatus>;
  /**
   * Demande un hot-swap vers un autre world Open-RMF (Bearer Cognito). Renvoie 202 fire-and-
   * forget : le caller doit ensuite poll /world-status jusqu'a `switching=false &&
   * current_world === target`. 409 si un switch est deja en cours, 400 si world hors allowlist.
   * Cf ADR 030 + control-plane-api.md § /switch-world.
   */
  switchWorld(world: string): Promise<SwitchWorldResult>;
  /**
   * Recupere le building_map upstream rmf-web. Contient les waypoints (vertices nommes) avec
   * leurs props pickup_dispenser/dropoff_ingestor/etc. utilisees pour valider les dispatch
   * et alimenter les selects du form.
   */
  getBuildingMap(): Promise<RmfBuildingMap>;
  /**
   * Liste les tasks (queued, underway, completed, etc.). REST `/api/tasks` paginated.
   * MVP : on prend la 1ere page (50 entries max), assez pour la demo. Pagination si besoin
   * en Phase 7.x.
   */
  listTasks(): Promise<RmfTask[]>;
  /**
   * Fetch le detail complet d'une task (GET /api/tasks/<id>/state). Inclut phases + active
   * phase + events, transformes en `RmfTaskDetail.activePhaseCategory` pour piloter les
   * effets visuels frontend par robot. Cache + poll a gerer cote tasksStore.
   */
  getTaskState(bookingId: string): Promise<RmfTaskDetail>;
  /**
   * Dispatch une nouvelle task. Payload pre-valide cote front via dispatch-validation.ts.
   * 200/201 = acceptee, 400 = payload invalide (Pydantic), 422 = combo refuse (fleet/waypoint).
   * Cf rmf-task-dispatch.md § POST /api/tasks/dispatch_task.
   *
   * `fleetName` :
   *   - string : force RMF a n'utiliser que cette fleet (rmf_api_msgs `request.fleet_name`).
   *     Si aucun robot dispo, la task reste queued/fail au lieu de tomber sur une autre fleet.
   *   - null : laisse le task_dispatcher RMF Core arbitrer entre toutes les fleets capables
   *     via le bidding process (comportement par defaut upstream).
   */
  dispatchTask(
    fleetName: string | null,
    request: DispatchTaskRequest,
    labels?: readonly string[]
  ): Promise<RmfTask>;
  /**
   * Annule une task en cours cote RMF. Le state de la task passera a `canceled` avec un
   * champ `cancellation.unix_millis_time + labels` (visible au prochain refresh listTasks).
   * Cf backend ticket 2026-05-28 § cancel : POST /api/tasks/cancel_task avec body
   * `{task_id, labels}`. 404 si la task n'existe pas, 409 si deja terminale.
   */
  cancelTask(bookingId: string, labels?: readonly string[]): Promise<void>;
  /** Annule la procedure d'attente cote UI (n'envoie pas de signal au backend). */
  cancel(): void;
}

const useMock = import.meta.env.VITE_BACKEND_MOCK !== 'false';
export const backendClient: BackendClient = useMock ? new MockBackendClient() : new RealBackendClient();
